import { useEffect, useRef, useState } from 'react'
import { Alert, Anchor, Button, Card, Code, Container, Group, Image, List, PinInput, Stack, Text, TextInput, Title } from '@mantine/core'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import backend from '../../backend/backend'
import { homeFor } from '../../components/shared/Guards'
import Message from '../../i18n/Message'
import { describeError, useT } from '../../hooks'
import { refreshMe, signOut } from '../../store/authActionCreators'

/** Second step of sign-in: authenticator code or a one-time recovery code. */
export function Verify() {
  const t = useT()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const status = useSelector((s) => s.auth.status)
  const user = useSelector((s) => s.auth.user)
  const [recovery, setRecovery] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  if (status === 'anon') return <Navigate to="/login" replace />
  if (status === 'ready' && user) return <Navigate to={homeFor(user.role)} replace />

  const submit = async (code) => {
    setBusy(true)
    setError(null)
    try {
      await backend.verifyMfa(recovery ? { recoveryCode: code } : { code })
      await dispatch(refreshMe())
      navigate(homeFor(user?.role), { replace: true })
    } catch (err) {
      setError(describeError(t, err))
      setValue('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Container size={420} py={80}>
      <Stack>
        <Title order={2}>
          <Message id="mfa_title" />
        </Title>
        <Text c="dimmed">
          <Message id={recovery ? 'mfa_recovery_hint' : 'mfa_hint'} />
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        {recovery ? (
          <Stack>
            <TextInput label={t('mfa_recovery_label')} value={value} onChange={(e) => setValue(e.currentTarget.value)} autoComplete="off" />
            <Button loading={busy} disabled={!value.trim()} onClick={() => submit(value.trim())}>
              <Message id="mfa_verify" />
            </Button>
          </Stack>
        ) : (
          <PinInput length={6} type="number" oneTimeCode value={value} onChange={setValue} onComplete={submit} disabled={busy} aria-label={t('mfa_code_label')} />
        )}
        <Group justify="space-between">
          <Anchor component="button" type="button" size="sm" onClick={() => { setRecovery(!recovery); setValue(''); setError(null) }}>
            <Message id={recovery ? 'mfa_use_app' : 'mfa_use_recovery'} />
          </Anchor>
          <Anchor component="button" type="button" size="sm" onClick={() => dispatch(signOut())}>
            <Message id="sign_out" />
          </Anchor>
        </Group>
      </Stack>
    </Container>
  )
}

/** Enrolment: QR + secret, confirm a code, then show the recovery codes once. */
export function SecuritySetup() {
  const t = useT()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const status = useSelector((s) => s.auth.status)
  const user = useSelector((s) => s.auth.user)
  const enrolled = useSelector((s) => s.auth.mfa.enrolled)
  const [setup, setSetup] = useState(null)
  const [codes, setCodes] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [value, setValue] = useState('')
  const started = useRef(false)

  const encryptionReady = useSelector((s) => s.auth.config?.encryptionConfigured !== false)

  useEffect(() => {
    if (status !== 'mfa' || enrolled || started.current) return
    started.current = true
    // Two-factor secrets are stored encrypted; without the owner's key there is nothing to start.
    if (!encryptionReady) {
      setError(t('config_encryption'))
      return
    }
    backend.startMfaEnrol().then(setSetup).catch((err) => setError(describeError(t, err)))
  }, [status, enrolled, encryptionReady, t])

  if (status === 'anon') return <Navigate to="/login" replace />
  if (status === 'mfa' && enrolled) return <Navigate to="/login/verify" replace />
  if (status === 'ready' && !codes) return <Navigate to={homeFor(user?.role)} replace />

  const confirm = async (code) => {
    setBusy(true)
    setError(null)
    try {
      const res = await backend.confirmMfaEnrol(code)
      setCodes(res.recoveryCodes)
    } catch (err) {
      setError(describeError(t, err))
      setValue('')
    } finally {
      setBusy(false)
    }
  }

  if (codes) {
    return (
      <Container size={460} py={60}>
        <Stack>
          <Title order={2}>
            <Message id="mfa_codes_title" />
          </Title>
          <Text c="dimmed">
            <Message id="mfa_codes_hint" />
          </Text>
          <Card>
            <List listStyleType="none" spacing={4}>
              {codes.map((c) => (
                <List.Item key={c}>
                  <Code fz="md">{c}</Code>
                </List.Item>
              ))}
            </List>
          </Card>
          <Button
            onClick={async () => {
              await dispatch(refreshMe())
              navigate(homeFor(user?.role), { replace: true })
            }}
          >
            <Message id="mfa_codes_saved" />
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size={460} py={60}>
      <Stack>
        <Title order={2}>
          <Message id="mfa_setup_title" />
        </Title>
        <Text c="dimmed">
          <Message id="mfa_setup_hint" />
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        {setup && (
          <Stack align="center">
            <Image src={setup.qr} w={220} h={220} alt={t('mfa_qr_alt')} />
            <Text size="sm" c="dimmed">
              <Message id="mfa_secret_label" />
            </Text>
            <Code fz="sm" style={{ wordBreak: 'break-all' }}>
              {setup.secret}
            </Code>
            <PinInput length={6} type="number" oneTimeCode value={value} onChange={setValue} onComplete={confirm} disabled={busy} aria-label={t('mfa_code_label')} />
          </Stack>
        )}
        <Anchor component="button" type="button" size="sm" onClick={() => dispatch(signOut())}>
          <Message id="sign_out" />
        </Anchor>
      </Stack>
    </Container>
  )
}
